/**
 * Master-data writes for callers outside React's render tree — the agent-chat
 * tool executor.
 *
 * Same endpoints the detail panels' own Edit forms call, so there is one write
 * path, one audit trail and one permission check (`mc.master_data.edit`, plus
 * `mc.customers.edit` for the customer create). What differs from the hooks in
 * `suppliersApi` / `articlesApi` / `productsApi` / `customersApi` is only the
 * plumbing: no `onSuccess` to hang cache work on and no toast wiring, because
 * the chat reports the outcome to the model instead of to the DOM. The cache
 * work is therefore asked for explicitly, via `invalidateMcEntityCaches`.
 *
 * The read helpers here exist for the confirmation dialogs. A dialog approving
 * a change has to show the record's REAL current values, not the ones the model
 * claimed — the model may be working from a remembered result, and the whole
 * point of the diff is that the user can check it. Same reasoning as
 * `describeRecipients` in the inquiry write path.
 */
import type { QueryClient } from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { invalidateMcEntityCaches } from "./mcQueryHelpers"
import type {
  ArticleComplianceOut,
  ArticleOut,
  ArticleSubstanceOut,
  CustomerOut,
  ProductOut,
  SubstanceOut,
  SupplierArticleRow,
  SupplierOut,
} from "./types/masterData"

const MASTER_DATA_BASE = "/material-compliance/master-data"
const SUPPLIERS = `${MASTER_DATA_BASE}/suppliers`
const ARTICLES = `${MASTER_DATA_BASE}/articles`
const PRODUCTS = `${MASTER_DATA_BASE}/products`
const CUSTOMERS = `${MASTER_DATA_BASE}/customers`
const SUBSTANCES = `${MASTER_DATA_BASE}/substances`
// The customer CREATE lives on its own router, which is also the only one
// requiring `mc.customers.edit` rather than `mc.master_data.edit`. Updates
// deliberately use the master-data router above instead of this one's PATCH:
// both call the same service with the same body, and routing every master-data
// edit through one permission is what keeps chat exactly as strict as the
// master-data panel it mirrors.
const CUSTOMERS_ROOT = "/material-compliance/customers"

/** The entity segment used in query keys, per entity kind. */
export type MasterDataEntity =
  | "suppliers"
  | "articles"
  | "products"
  | "customers"

// ─── Reads, for confirmation dialogs ─────────────────────────────────────────

export async function fetchSupplierDetail(id: string): Promise<SupplierOut> {
  return apiClientV2.get<SupplierOut>(`${SUPPLIERS}/${id}`).then((r) => r.data)
}

export async function fetchArticleDetail(id: string): Promise<ArticleOut> {
  return apiClientV2.get<ArticleOut>(`${ARTICLES}/${id}`).then((r) => r.data)
}

export async function fetchProductDetail(id: string): Promise<ProductOut> {
  return apiClientV2.get<ProductOut>(`${PRODUCTS}/${id}`).then((r) => r.data)
}

export async function fetchCustomerDetail(id: string): Promise<CustomerOut> {
  return apiClientV2.get<CustomerOut>(`${CUSTOMERS}/${id}`).then((r) => r.data)
}

export async function fetchArticleSuppliers(
  articleId: string,
): Promise<SupplierArticleRow[]> {
  return apiClientV2
    .get<SupplierArticleRow[]>(`${ARTICLES}/${articleId}/suppliers`)
    .then((r) => r.data)
}

export async function fetchArticleCompliance(
  articleId: string,
): Promise<ArticleComplianceOut[]> {
  return apiClientV2
    .get<ArticleComplianceOut[]>(`${ARTICLES}/${articleId}/compliance`)
    .then((r) => r.data)
}

/** The record's own name, for verifying the id the model passed. */
export async function fetchRecordName(
  entity: MasterDataEntity,
  id: string,
): Promise<string | null> {
  const base = {
    suppliers: SUPPLIERS,
    articles: ARTICLES,
    products: PRODUCTS,
    customers: CUSTOMERS,
  }[entity]
  const record = await apiClientV2
    .get<{ name?: string | null }>(`${base}/${id}`)
    .then((r) => r.data)
  return record.name ?? null
}

// ─── Field writes ────────────────────────────────────────────────────────────

// `changes` is built by the caller from the model's arguments, already filtered
// to the fields the tool schema allows. Sent as-is: apiClientV2 handles the
// camelCase→snake_case conversion these endpoints expect.

export async function patchSupplierDirect(
  qc: QueryClient,
  id: string,
  changes: Record<string, unknown>,
): Promise<SupplierOut> {
  const saved = await apiClientV2
    .patch<SupplierOut>(`${SUPPLIERS}/${id}`, changes)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "suppliers", id)
  return saved
}

export async function patchArticleDirect(
  qc: QueryClient,
  id: string,
  changes: Record<string, unknown>,
): Promise<ArticleOut> {
  const saved = await apiClientV2
    .patch<ArticleOut>(`${ARTICLES}/${id}`, changes)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "articles", id)
  return saved
}

export async function patchProductDirect(
  qc: QueryClient,
  id: string,
  changes: Record<string, unknown>,
): Promise<ProductOut> {
  const saved = await apiClientV2
    .patch<ProductOut>(`${PRODUCTS}/${id}`, changes)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "products", id)
  return saved
}

export async function patchCustomerDirect(
  qc: QueryClient,
  id: string,
  changes: Record<string, unknown>,
): Promise<CustomerOut> {
  const saved = await apiClientV2
    .patch<CustomerOut>(`${CUSTOMERS}/${id}`, changes)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "customers", id)
  return saved
}

export async function createCustomerDirect(
  qc: QueryClient,
  body: Record<string, unknown>,
): Promise<CustomerOut> {
  const created = await apiClientV2
    .post<CustomerOut>(CUSTOMERS_ROOT, body)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "customers", created.id)
  return created
}

/**
 * Creates a master-data substance. Two independent list caches read this
 * entity — the org-scoped ["mc","substances","list",...] (dropped by
 * invalidateMcEntityCaches) and the unscoped catalog
 * ["mc","substances","global","list",...] the declaration-review substance
 * picker reads from — so both need dropping. Mirrors `useCreateSubstance` in
 * substancesApi.ts, the React-hook variant of this same write.
 */
export async function createSubstanceDirect(
  qc: QueryClient,
  body: Record<string, unknown>,
): Promise<SubstanceOut> {
  const created = await apiClientV2
    .post<SubstanceOut>(SUBSTANCES, body)
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "substances", created.id)
  qc.invalidateQueries({ queryKey: ["mc", "substances", "global"] })
  return created
}

// ─── Article ↔ supplier links ────────────────────────────────────────────────

/**
 * A link write moves rows on the ARTICLE's surfaces and the SUPPLIER's alike —
 * the supplier's article list gains or loses this part — so entity-scoped
 * invalidation is not enough here. The whole `["mc", "suppliers"]` prefix goes
 * too, which prefix matching makes one line.
 */
function invalidateLinkCaches(qc: QueryClient, articleId: string): void {
  invalidateMcEntityCaches(qc, "articles", articleId)
  qc.invalidateQueries({ queryKey: ["mc", "articles", articleId, "suppliers"] })
  qc.invalidateQueries({ queryKey: ["mc", "suppliers"] })
}

export async function addArticleSupplierDirect(
  qc: QueryClient,
  articleId: string,
  supplierId: string,
  supplierArticleNumber: string | null,
): Promise<SupplierArticleRow> {
  const link = await apiClientV2
    .post<SupplierArticleRow>(`${ARTICLES}/${articleId}/suppliers`, {
      supplier_id: supplierId,
      supplier_article_number: supplierArticleNumber,
    })
    .then((r) => r.data)
  invalidateLinkCaches(qc, articleId)
  return link
}

export async function updateArticleSupplierDirect(
  qc: QueryClient,
  articleId: string,
  supplierId: string,
  changes: Record<string, unknown>,
): Promise<SupplierArticleRow> {
  const link = await apiClientV2
    .patch<SupplierArticleRow>(
      `${ARTICLES}/${articleId}/suppliers/${supplierId}`,
      changes,
    )
    .then((r) => r.data)
  invalidateLinkCaches(qc, articleId)
  return link
}

export async function removeArticleSupplierDirect(
  qc: QueryClient,
  articleId: string,
  supplierId: string,
): Promise<void> {
  await apiClientV2.delete(`${ARTICLES}/${articleId}/suppliers/${supplierId}`)
  invalidateLinkCaches(qc, articleId)
}

// ─── Article ↔ substance links ───────────────────────────────────────────────

/**
 * Same "both sides move" reasoning as `invalidateLinkCaches` above, but for a
 * SUBSTANCE link: linking/unlinking a substance moves the substance's own
 * surfaces too (its used-in-articles count/list), not just the article's —
 * unlike a supplier link, which is article/supplier-only. `substanceId` is
 * optional because a removal only ever carries `linkId`/`articleId`; when it
 * is absent, the substance-side invalidation falls back to the shared list
 * prefix, which still covers both the org-scoped and global-catalog
 * substance lists.
 */
function invalidateSubstanceLinkCaches(
  qc: QueryClient,
  articleId: string,
  substanceId?: string,
): void {
  invalidateMcEntityCaches(qc, "articles", articleId)
  qc.invalidateQueries({
    queryKey: ["mc", "articles", articleId, "substances"],
  })
  qc.invalidateQueries({ queryKey: ["mc", "substances"] })
  if (substanceId) {
    qc.invalidateQueries({
      queryKey: ["mc", "substances", "detail", substanceId],
    })
    qc.invalidateQueries({
      queryKey: ["mc", "substances", substanceId, "articles"],
    })
  }
}

/**
 * Deliberately takes no `exemption`/`exemptionCode`, matching LinkSubstanceDialog:
 * a RoHS Annex III/IV exemption is scoped to a category of EEE, so whether one
 * applies is a property of the PRODUCT, not the article. The same article can
 * sit in an electronic product (RoHS applies) and a non-electronic one (RoHS
 * does not apply at all), and one article-level value cannot be right for both.
 *
 * Leaving the parameters here would keep that claim assertable through the chat
 * write path while the UI refuses it. The backend's ArticleSubstanceCreateIn
 * defaults both to False/None, so omitting them is a valid payload; the
 * declaration and import paths still populate the columns with what a supplier
 * actually declared.
 */
export async function addArticleSubstanceDirect(
  qc: QueryClient,
  articleId: string,
  substanceId: string,
  supplierId: string,
  concentrationPct: number | null,
): Promise<ArticleSubstanceOut> {
  const link = await apiClientV2
    .post<ArticleSubstanceOut>(`${ARTICLES}/${articleId}/substances`, {
      substance_id: substanceId,
      supplier_id: supplierId,
      concentration_pct: concentrationPct,
    })
    .then((r) => r.data)
  invalidateSubstanceLinkCaches(qc, articleId, substanceId)
  return link
}

export async function removeArticleSubstanceDirect(
  qc: QueryClient,
  articleId: string,
  linkId: string,
): Promise<void> {
  await apiClientV2.delete(`${ARTICLES}/${articleId}/substances/${linkId}`)
  invalidateSubstanceLinkCaches(qc, articleId)
}

// ─── Compliance verdict override ─────────────────────────────────────────────

/**
 * `source` is not sent at all: the service now ignores whatever a caller puts
 * there and forces "manual" itself (mc_master_data_service.py,
 * update_article_compliance) — mirroring how the create endpoint has always
 * hardcoded it, and matching what the status dropdown already claims happens.
 * Sending it here would be misleading dead weight, not a provenance claim.
 */
export async function overrideArticleComplianceDirect(
  qc: QueryClient,
  articleId: string,
  complianceId: string,
  status: string,
  exemptionCode: string | null,
): Promise<ArticleComplianceOut> {
  const saved = await apiClientV2
    .put<ArticleComplianceOut>(
      `${ARTICLES}/${articleId}/compliance/${complianceId}`,
      {
        status,
        exemptionCode,
      },
    )
    .then((r) => r.data)
  invalidateMcEntityCaches(qc, "articles", articleId)
  qc.invalidateQueries({
    queryKey: ["mc", "articles", articleId, "compliance"],
  })
  return saved
}
